import { CommonModule, DOCUMENT } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule, inject, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CareersPageLibraryModule } from 'talos-careers-page-library';
import { CoreModule } from 'talos-core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ShowNavbarPipe } from './app.pipes';
import { CheckCancelledPage } from './pages/check-cancelled/check-cancelled.component';
import { DynamicPagePage } from './pages/dynamic-page/dynamic-page.page';
import { CallToActionService } from './pages/dynamic-page/services/call-to-action.service';
import { InvalidComponent } from './pages/invalid/invalid.component';
import { LoginPage } from './pages/login/login.page';
import { RefereeThankyouPage } from './pages/referee-thankyou/referee-thankyou.component';
import { RefereePageComponent } from './pages/referee/referee.page';
import { RefereeCompletedComponent } from './pages/referencing/components/referee-completed/referee-completed.component';
import { RefereePendingComponent } from './pages/referencing/components/referee-pending/referee-pending.component';
import { ReferencingDashboardComponent } from './pages/referencing/components/referencing-dashboard/referencing-dashboard.component';
import { ReferencingRefereeFormComponent } from './pages/referencing/components/referencing-referee-form/referencing-referee-form.component';
import { ReferencingPage } from './pages/referencing/referencing.page';
import { BrandingService } from './services/branding.service';
import { ConfigService } from './services/config.service';
import { DynamicPageService } from './services/dynamic-page.service';
import { ErrorHandlerService } from './services/error-handler.service';
import { HttpService } from './services/http.service';
import { LoginService } from './services/login.service';
import { MetaDataService } from './services/meta-data.service';
import { OfferDetailsService } from './services/offer-details.service';
import { SuccessService } from './services/success.service';
import { InterviewsPage } from './pages/interviews/interviews.page';
import { InterviewDashboardComponent } from './pages/interviews/components/interview-dashboard/interview-dashboard.component';
import { InterviewDashboardCardPendingComponent } from './pages/interviews/components/interview-dashboard-cards/interview-dashboard-card-pending/interview-dashboard-card-pending.component';
import { InterviewDashboardCardConfirmedComponent } from './pages/interviews/components/interview-dashboard-cards/interview-dashboard-card-confirmed/interview-dashboard-card-confirmed.component';
import { InterviewDashboardCardCancelledComponent } from './pages/interviews/components/interview-dashboard-cards/interview-dashboard-card-cancelled/interview-dashboard-card-cancelled.component';
import { DateService } from './services/date.service';
import { GoogleMapsModule } from '@angular/google-maps';
import { InterviewLocationWidgetComponent } from './pages/interviews/components/widgets/interview-location-widget/interview-location-widget.component';
import { InterviewTimeWidgetComponent } from './pages/interviews/components/widgets/interview-time-widget/interview-time-widget.component';
import { InterviewDateWidgetComponent } from './pages/interviews/components/widgets/interview-date-widget/interview-date-widget.component';
import { InterviewDetailsGroupComponent } from './pages/interviews/components/interview-details-pages/interview-details-group/interview-details-group.component';
import { InterviewDetailsIndividualSelectComponent } from './pages/interviews/components/interview-details-pages/interview-details-individual-select/interview-details-individual-select.component';
import { InterviewDetailsIndividualSpecificComponent } from './pages/interviews/components/interview-details-pages/interview-details-individual-specific/interview-details-individual-specific.component';
import { InterviewDetailsConfirmedComponent } from './pages/interviews/components/interview-details-pages/interview-details-confirmed/interview-details-confirmed.component';
import { DeclineInterviewWidgetComponent } from './pages/interviews/components/widgets/decline-interview-widget/decline-interview-widget.component';
import { InterviewLocationMapWidgetComponent } from './pages/interviews/components/widgets/interview-location-map-widget/interview-location-map-widget.component';
import { ToastAlertComponent } from './elements/toast/toast.element';
import { InterviewListViewPage } from './pages/interviews/pages/interview-list-view/interview-list-view.page';
import { InterviewDetailsPage } from './pages/interviews/pages/interview-details/interview-details.page';
import { InterviewsService } from './pages/interviews/services/interviews.service';
import { CanAccessOnboardingGuard } from './guards/can-access-onboarding.guard';
import { CanAccessInterviewsGuard } from './guards/can-access-interviews.guard';
import { CanAccessReferencingGuard } from './guards/can-access-referencing.guard';
import { TalosFormsModule } from '@talos360/talos-forms';
import { GroupSlotComponent } from './pages/interviews/components/interview-details-pages/interview-details-group/group-slot/group-slot.component';

@NgModule({ declarations: [
        AppComponent,
        CheckCancelledPage,
        LoginPage,
        RefereePageComponent,
        ReferencingPage,
        RefereeCompletedComponent,
        RefereePendingComponent,
        ReferencingDashboardComponent,
        RefereeThankyouPage,
        ReferencingRefereeFormComponent,
        InvalidComponent,
        DynamicPagePage,
        ToastAlertComponent,
        ShowNavbarPipe,
        InterviewsPage,
        InterviewDashboardComponent,
        InterviewDashboardCardPendingComponent,
        InterviewDashboardCardConfirmedComponent,
        InterviewDashboardCardCancelledComponent,
        InterviewDetailsIndividualSpecificComponent,
        InterviewDetailsIndividualSelectComponent,
        InterviewDetailsGroupComponent,
        InterviewDetailsConfirmedComponent,
        InterviewLocationWidgetComponent,
        InterviewTimeWidgetComponent,
        InterviewDateWidgetComponent,
        DeclineInterviewWidgetComponent,
        InterviewLocationMapWidgetComponent,
        InterviewListViewPage,
        InterviewDetailsPage,
        InterviewDetailsGroupComponent,
        GroupSlotComponent        
    ],
    bootstrap: [
        AppComponent
    ], 
    imports: [
        BrowserModule,
        AppRoutingModule,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        CareersPageLibraryModule,
        CoreModule,
        BrowserAnimationsModule,
        GoogleMapsModule,
        TalosFormsModule
    ], 
    providers: [
        provideHttpClient(withInterceptorsFromDi()),        
        provideAppInitializer(() => {
            const configService = inject(ConfigService);
            return configService.loadSettings();
        }),
        { provide: DOCUMENT, useValue: document },
        BrandingService,
        HttpService,
        DynamicPageService,
        CallToActionService,
        MetaDataService,
        SuccessService,
        ErrorHandlerService,
        OfferDetailsService,
        LoginService,
        DateService,
        InterviewsService,
        CanAccessOnboardingGuard,
        CanAccessInterviewsGuard,
        CanAccessReferencingGuard,
    ] })
export class AppModule { }
